import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Popup } from '../components';
import { purpleIcon } from '../assets';
import './PriceModel.css';

// The price model, served at /price-model (it replaced the original
// YouTube-only synchronous tool, PriceModel.js, which is no longer routed;
// /price-model-b, where this page first lived, redirects here). Covers
// YouTube, Instagram, and TikTok, with every analysis delivered by email:
// the server pulls the stats and emails a /price-model?run=<uid> link that
// loads the results back into this page, where the CPM slider and
// calculation type stay live.
//
// The browser key below is only used for the channel *lookup/picker* (so a
// YouTube link resolves to a confirmed channel before a run is spent) and as
// the legacy synchronous fallback when the server pipeline isn't deployed.
// Without it, YouTube falls back to manual expected-views entry as before.
const YT_API_KEY = process.env.REACT_APP_YT_API_KEY || '';

// Same-origin serverless API (Vercel functions under /api backed by Neon).
// It owns the lead_capture table, the run counting, the privileged-email
// list, and the Slack alerts — none of which ship in this bundle. When the
// API isn't deployed/configured (local npm start, missing DATABASE_URL), the
// page degrades to its client-side behavior.
const API_BASE = process.env.REACT_APP_PRICE_MODEL_API || '/api/price-model';

const api = async (path, body) => {
    const res = await fetch(`${API_BASE}/${path}`, {
        method: 'POST',
        headers: { 'Content-type': 'application/json' },
        body: JSON.stringify(body),
    });
    if (!res.ok) {
        const data = await res.json().catch(() => null);
        const err = new Error((data && data.error) || `API ${path} failed`);
        err.status = res.status;
        throw err;
    }
    return res.json();
};

// Fallback when the API is unavailable: leads flow into the same Retool
// workflow the waitlist form uses; `source` distinguishes them downstream.
const LEAD_WEBHOOK =
    process.env.REACT_APP_PRICE_MODEL_LEAD_WEBHOOK ||
    'https://api.retool.com/v1/workflows/249eb0ca-9e58-4f6e-8f81-a35c5628da34/startTrigger?workflowApiKey=retool_wk_0888ffad5566497187f743a56b728409';
const LEAD_SOURCE = 'free-model-tool';

// Offline testing bypass for when the API isn't deployed: the SHA-256 hex
// hash of a single bypass email can be baked in at build time. Entering a
// matching email skips the lead form and the run limit. Only the hash is
// public — but hashes of guessable addresses can be brute-forced offline, so
// use a hard-to-guess address. With the API deployed, prefer putting the
// address in the server-side PRICE_MODEL_UNLIMITED_EMAILS list instead.
const TEST_EMAIL_HASH = (process.env.REACT_APP_TEST_EMAIL_HASH || '').toLowerCase();

const LEAD_KEY = 'tr_pm_lead';
const RUNS_KEY = 'tr_pm_runs';
const DAILY_RUN_LIMIT = 5;

const MAX_VIDEO_COUNT = 10;
const CPM_MIN = 1;
const CPM_MAX = 200;

const CONTENT_TYPES = [
    { value: 'any', label: 'Any Type' },
    { value: 'only-longform', label: 'Longform' },
    { value: 'only-shorts', label: 'Shorts' },
];

const BUSINESS_TYPES = [
    { value: 'brand', label: 'Brand' },
    { value: 'agent', label: 'Creator representation (e.g., talent agent, management, etc)' },
    { value: 'brand-agency', label: 'Ad Agency or other brand agency' },
    { value: 'creator', label: 'Creator' },
    { value: 'other', label: 'Other' },
];

// ----- Helpers ---------------------------------------------------------------

const number = (n) => (isFinite(n) ? Math.round(n).toLocaleString('en-US') : '0');

const currency = (n) =>
    isFinite(n)
        ? n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 })
        : '$0.00';

const compact = (n) =>
    isFinite(n) ? n.toLocaleString('en-US', { notation: 'compact', maximumFractionDigits: 1 }) : '—';

const percent = (n) => (isFinite(n) ? `${(n * 100).toFixed(1)}%` : '0%');

const num = (v) => {
    const n = parseFloat(v);
    return isFinite(n) ? n : 0;
};

const average = (arr) => (arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : 0);

const median = (arr) => {
    if (!arr.length) return 0;
    const s = [...arr].sort((a, b) => a - b);
    const m = Math.floor(s.length / 2);
    return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2;
};

const readStorage = (key) => {
    try {
        return JSON.parse(localStorage.getItem(key));
    } catch (e) {
        return null;
    }
};

const writeStorage = (key, value) => {
    try {
        localStorage.setItem(key, JSON.stringify(value));
    } catch (e) {
        // Storage unavailable (private mode) — the tool still works, gating just resets.
    }
};

const today = () => new Date().toISOString().slice(0, 10);

const readRunsToday = () => {
    const r = readStorage(RUNS_KEY);
    return r && r.date === today() ? r.count : 0;
};

const sha256Hex = async (text) => {
    const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text));
    return Array.from(new Uint8Array(buf))
        .map((b) => b.toString(16).padStart(2, '0'))
        .join('');
};

const matchesTestEmail = async (value) => {
    if (!TEST_EMAIL_HASH) return false;
    try {
        return (await sha256Hex(value)) === TEST_EMAIL_HASH;
    } catch (e) {
        // crypto.subtle needs a secure context; without it there's no bypass.
        return false;
    }
};

// ----- Link parsing (ported from the Retool inputTransformer) ----------------

const extractIdentifier = (input) => {
    if (!/youtube\.com/i.test(input)) return null;
    const channelRegex = /(?:https?:\/\/)?(?:www\.)?youtube\.com\/(?:channel\/([A-Za-z0-9_-]{24})|c\/([A-Za-z0-9_-]+)|user\/([A-Za-z0-9_-]+)|@([A-Za-z0-9_.-]+)|([A-Za-z0-9_-]+))/i;
    const m = input.match(channelRegex);
    if (m) {
        if (m[1]) return { type: 'id', value: m[1] };
        if (m[3]) return { type: 'forHandle', value: m[3] };
        if (m[4]) return { type: 'forHandle', value: m[4] };
        if (m[5]) return { type: 'forHandle', value: m[5] };
        if (m[2]) return { type: 'search', value: m[2] };
    }
    return { type: 'search', value: input };
};

// Instagram profile URLs are just /<handle>; these path segments are site
// pages, not profiles (a reel/post link still identifies the profile poorly,
// so we ask for the profile link itself).
const IG_RESERVED = ['p', 'reel', 'reels', 'tv', 'stories', 'explore', 'accounts', 'direct'];

// Classifies a pasted link as YouTube / Instagram / TikTok (the monolith's
// link-detection behavior, ported): IG/TikTok resolve to a handle purely from
// the URL — no network — while YouTube keeps its lookup/search flow.
const detectLink = (input) => {
    if (/instagram\.com/i.test(input)) {
        const m = input.match(/instagram\.com\/@?([A-Za-z0-9_.]+)/i);
        if (m && !IG_RESERVED.includes(m[1].toLowerCase())) {
            return { platform: 'Instagram', handle: m[1] };
        }
        return null;
    }
    if (/tiktok\.com/i.test(input)) {
        const m = input.match(/tiktok\.com\/@([A-Za-z0-9_.-]+)/i);
        if (m) return { platform: 'TikTok', handle: m[1] };
        return null;
    }
    const ident = extractIdentifier(input);
    return ident ? { platform: 'YouTube', ident } : null;
};

const profileChannel = (platform, handle) => {
    const h = handle.replace(/^@/, '').replace(/\/+$/, '').toLowerCase();
    return {
        platform,
        channelId: h,
        channelTitle: `@${h}`,
        channelLink:
            platform === 'Instagram'
                ? `https://www.instagram.com/${h}/`
                : `https://www.tiktok.com/@${h}`,
        // Shown on the confirmation chip in place of a channel description.
        channelDescription: platform === 'Instagram' ? 'Instagram profile' : 'TikTok profile',
    };
};

// ----- YouTube Data API ------------------------------------------------------

const ytFetch = async (endpoint, params) => {
    const qs = new URLSearchParams({ ...params, key: YT_API_KEY });
    const res = await fetch(`https://www.googleapis.com/youtube/v3/${endpoint}?${qs.toString()}`);
    const data = await res.json();
    if (!res.ok) {
        const err = new Error(data.error?.message || 'YouTube API request failed.');
        err.status = res.status;
        throw err;
    }
    return data;
};

const mapChannel = (item) => ({
    channelId: item.id?.channelId || item.id,
    channelTitle: item.snippet.title,
    channelLink: `https://www.youtube.com/channel/${item.id?.channelId || item.id}`,
    image: item.snippet.thumbnails?.high?.url || item.snippet.thumbnails?.default?.url,
    channelDescription: item.snippet.description,
});

const findYouTubeChannels = async (ident) => {
    if (ident.type === 'id') {
        const data = await ytFetch('channels', { part: 'snippet', id: ident.value });
        return (data.items || []).map(mapChannel);
    }
    if (ident.type === 'forHandle') {
        const data = await ytFetch('channels', { part: 'snippet', forHandle: ident.value });
        if (data.items?.length) return data.items.map(mapChannel);
        // Not a real handle (old custom URL etc.) — fall through to search.
    }
    const data = await ytFetch('search', {
        part: 'snippet',
        type: 'channel',
        maxResults: '5',
        q: ident.value,
    });
    return (data.items || []).map(mapChannel);
};

// Content type filtering uses YouTube's special upload playlists, exactly like
// the Expected Views workflow: UU = all uploads, UUSH = Shorts only,
// UULF = longform only. The video count caps the pull itself via maxResults.
const PLAYLIST_PREFIX = {
    'any': 'UU',
    'only-shorts': 'UUSH',
    'only-longform': 'UULF',
};

const fetchYouTubeVideos = async (channel, contentType, count) => {
    const prefix = PLAYLIST_PREFIX[contentType] || 'UU';
    const playlistId = prefix + channel.channelId.slice(-22);
    let playlist;
    try {
        playlist = await ytFetch('playlistItems', {
            part: 'contentDetails',
            playlistId,
            maxResults: String(count),
        });
    } catch (err) {
        // The UUSH/UULF playlists 404 when the channel has no videos of that type.
        if (err.status === 404) return [];
        throw err;
    }
    const ids = (playlist.items || []).map((i) => i.contentDetails.videoId);
    if (!ids.length) return [];
    const data = await ytFetch('videos', {
        part: 'snippet,statistics',
        id: ids.join(','),
    });
    return (data.items || [])
        // The workflow excludes live broadcasts from the consideration set.
        .filter((video) => video.snippet.liveBroadcastContent === 'none')
        .map((video) => {
            const views = parseInt(video.statistics.viewCount || 0, 10);
            const likes = parseInt(video.statistics.likeCount || 0, 10);
            const comments = parseInt(video.statistics.commentCount || 0, 10);
            return {
                videoId: video.id,
                videoTitle: video.snippet.title,
                videoLink: `https://youtube.com/watch?v=${video.id}`,
                publishDate: video.snippet.publishedAt,
                thumbnail: video.snippet.thumbnails?.medium?.url,
                viewCount: views,
                likeCount: likes,
                commentCount: comments,
                engageRate: views > 0 ? (likes + comments) / views : 0,
            };
        });
};

// ----- Small building blocks -------------------------------------------------

const Field = ({ label, suffix, ...props }) => (
    <label className="pm-field">
        <span>{label}</span>
        <div className="pm-input-wrap">
            <input {...props} />
            {suffix && <span className="pm-suffix">{suffix}</span>}
        </div>
    </label>
);

// Dual-thumb CPM range slider (the Retool RangeSlider, 1-200).
const CpmSlider = ({ low, high, onChange }) => {
    const lo = num(low);
    const hi = num(high);
    const pos = (v) => ((v - CPM_MIN) / (CPM_MAX - CPM_MIN)) * 100;
    return (
        <div className="pm-slider-field">
            <div className="pm-slider-head">
                <span className="pm-slider-label">CPM range</span>
                <span className="pm-slider-value">{lo}–{hi}</span>
            </div>
            <span className="pm-slider-caption">in USD</span>
            <div className="pm-slider">
                <div
                    className="pm-slider-fill"
                    style={{ left: `${pos(lo)}%`, width: `${pos(hi) - pos(lo)}%` }}
                />
                <input
                    type="range"
                    min={CPM_MIN}
                    max={CPM_MAX}
                    step="1"
                    value={lo}
                    aria-label="CPM floor"
                    onChange={(e) => onChange(Math.min(num(e.target.value), hi - 1), hi)}
                />
                <input
                    type="range"
                    min={CPM_MIN}
                    max={CPM_MAX}
                    step="1"
                    value={hi}
                    aria-label="CPM ceiling"
                    onChange={(e) => onChange(lo, Math.max(num(e.target.value), lo + 1))}
                />
            </div>
        </div>
    );
};

const StatTile = ({ label, value, caption }) => (
    <div className="pm-stat">
        <span className="pm-stat-label">{label}</span>
        <strong className="pm-stat-value">{value}</strong>
        {caption && <span className="pm-stat-caption">{caption}</span>}
    </div>
);

// Channel picker (the Retool creatorSearchSelect): a dropdown with avatars
// when a search returned multiple candidates; a plain confirmation chip when
// a handle/ID pull resolved exactly one channel.
const ChannelPicker = ({ options, selected, onSelect }) => {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    const single = options.length <= 1;

    useEffect(() => {
        if (!open) return;
        const close = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', close);
        return () => document.removeEventListener('mousedown', close);
    }, [open]);

    return (
        <div className="pm-picker" ref={ref}>
            <button
                type="button"
                className={`pm-picker-toggle ${single ? 'pm-picker-static' : ''}`}
                onClick={() => !single && setOpen(!open)}
                aria-haspopup="listbox"
                aria-expanded={open}
            >
                {selected?.image && <img src={selected.image} alt="" />}
                <span className="pm-channel-meta">
                    <strong>{selected?.channelTitle}</strong>
                    {selected?.channelDescription && <em>{selected.channelDescription}</em>}
                </span>
                {!single && (
                    <svg
                        className="pm-picker-caret"
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    >
                        <path d="m6 9 6 6 6-6" />
                    </svg>
                )}
            </button>
            {open && (
                <div className="pm-picker-list" role="listbox">
                    {options.map((c) => (
                        <button
                            key={c.channelId}
                            type="button"
                            role="option"
                            aria-selected={selected?.channelId === c.channelId}
                            className={`pm-channel ${selected?.channelId === c.channelId ? 'pm-channel-active' : ''}`}
                            onClick={() => {
                                onSelect(c);
                                setOpen(false);
                            }}
                        >
                            {c.image && <img src={c.image} alt="" />}
                            <span className="pm-channel-meta">
                                <strong>{c.channelTitle}</strong>
                                {c.channelDescription && <em>{c.channelDescription}</em>}
                            </span>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

// ----- Lead capture (ported from the Retool leadCaptureModal) ----------------

const LeadCaptureForm = ({ channelName, onDone }) => {
    const [email, setEmail] = useState('');
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [website, setWebsite] = useState('');
    const [profile, setProfile] = useState('');
    const [message, setMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [checking, setChecking] = useState(false);
    const [found, setFound] = useState(false);

    // Skip the rest of the form: store the lead locally and continue the run.
    const bypass = (returning, unlimited) => {
        const lead = {
            email: email.trim().toLowerCase(),
            source: LEAD_SOURCE,
            returning,
            unlimited: !!unlimited,
        };
        writeStorage(LEAD_KEY, lead);
        onDone(lead);
    };

    // On blur, check whether this lead already exists. The API does the Neon
    // lookup and fires the Slack reuse alert; if it reports the lead as
    // known, we let them straight through (Retool emailLookup parity).
    const onEmailBlur = async () => {
        const value = email.trim().toLowerCase();
        if (!value.includes('@') || checking || found) return;
        if (await matchesTestEmail(value)) {
            bypass(false, true);
            return;
        }
        setChecking(true);
        try {
            const data = await api('lookup', { email: value, channel: channelName || null });
            if (data?.found) {
                setFound(true);
                // Let the "found" state show briefly before continuing (Retool
                // hid its modal on a ~1s debounce after the email lookup hit).
                setTimeout(() => bypass(true, !!data.unlimited), 900);
            }
        } catch (err) {
            // Lookup unavailable — carry on with the normal form.
        }
        setChecking(false);
    };

    const submit = async (e) => {
        e.preventDefault();
        if (await matchesTestEmail(email.trim().toLowerCase())) {
            bypass(false, true);
            return;
        }
        if (found) return;
        if (!email || !firstName || !lastName) {
            setErrorMessage('Please fill out the required fields.');
            return;
        }
        if (!email.includes('@')) {
            setErrorMessage('Email input invalid.');
            return;
        }
        const payload = {
            email: email.toLowerCase(),
            first_name: firstName,
            last_name: lastName,
            website,
            profile,
            message,
            source: LEAD_SOURCE,
        };
        let unlimited = false;
        try {
            // The API stores the lead in Neon and sends the Slack alert.
            const data = await api('lead', payload);
            unlimited = !!data?.unlimited;
        } catch (err) {
            // API unavailable — fall back to the Retool webhook so the lead
            // isn't lost. Fire-and-forget: a hiccup shouldn't block the tool.
            fetch(LEAD_WEBHOOK, {
                method: 'POST',
                headers: { 'Content-type': 'application/json' },
                body: JSON.stringify(payload),
            }).catch(() => {});
        }
        const lead = { email: payload.email, source: LEAD_SOURCE, unlimited };
        writeStorage(LEAD_KEY, lead);
        onDone(lead);
    };

    return (
        <div className="pm-lead">
            <div className="pm-lead-head">
                <img src={purpleIcon} className="pm-lead-icon" alt="TrueRep" />
                <h3>Your analysis{channelName ? ` of ${channelName}` : ''} is almost yours</h3>
                <p>Tell us a bit about yourself and we'll show you your results.</p>
            </div>
            <form className="pm-lead-form" onSubmit={submit}>
                <input
                    type="email"
                    placeholder="Your email *"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => {
                        setEmail(e.target.value);
                        setFound(false);
                    }}
                    onBlur={onEmailBlur}
                    style={errorMessage && (!email || !email.includes('@')) ? { outline: '3px solid #FF6F61' } : null}
                />
                {(checking || found) && (
                    <span className={`pm-lead-status ${found ? 'pm-lead-status-found' : ''}`}>
                        {found ? 'Found you — showing your results…' : 'Checking…'}
                    </span>
                )}
                <div className="pm-lead-row">
                    <input
                        type="text"
                        placeholder="Your first name *"
                        autoComplete="given-name"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        style={errorMessage && !firstName ? { outline: '3px solid #FF6F61' } : null}
                    />
                    <input
                        type="text"
                        placeholder="Your last name *"
                        autoComplete="family-name"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        style={errorMessage && !lastName ? { outline: '3px solid #FF6F61' } : null}
                    />
                </div>
                <input
                    type="url"
                    placeholder="Your business website"
                    autoComplete="organization"
                    value={website}
                    onChange={(e) => setWebsite(e.target.value)}
                />
                <select value={profile} onChange={(e) => setProfile(e.target.value)}>
                    <option value="" disabled>Type of business</option>
                    {BUSINESS_TYPES.map((b) => (
                        <option key={b.value} value={b.value}>{b.label}</option>
                    ))}
                </select>
                <textarea
                    rows="3"
                    placeholder="What brought you here? Anything else you'd like us to know?"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                />
                {errorMessage && <i className="pm-lead-error">{errorMessage}</i>}
                <button type="submit" className="pm-btn">Submit</button>
            </form>
        </div>
    );
};

// ----- Main page -------------------------------------------------------------

export const PriceModelB = () => {
    // Creator lookup
    const [link, setLink] = useState('');
    const [channelOptions, setChannelOptions] = useState([]);
    const [selectedChannel, setSelectedChannel] = useState(null);
    const [manualMode, setManualMode] = useState(false);
    const [manualNote, setManualNote] = useState('');
    const [lookupLoading, setLookupLoading] = useState(false);

    // Analysis parameters
    const [contentType, setContentType] = useState('any');
    const [videoCount, setVideoCount] = useState('5');
    const [calcType, setCalcType] = useState('average');
    const [manualViews, setManualViews] = useState('');
    const [manualEngage, setManualEngage] = useState('');

    // CPM range (slider)
    const [cpmMin, setCpmMin] = useState('10');
    const [cpmMax, setCpmMax] = useState('100');

    // Run state
    const [fetched, setFetched] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    // Set once a run has been queued for email delivery: {email, channelTitle}.
    const [queuedNotice, setQueuedNotice] = useState(null);
    // Emailed results link: /price-model?run=<uid> hydrates the page below.
    const [searchParams] = useSearchParams();
    const runUid = searchParams.get('run');

    // Lead gate
    const [lead, setLead] = useState(() => readStorage(LEAD_KEY));
    const [leadOpen, setLeadOpen] = useState(false);
    const [runsToday, setRunsToday] = useState(() => readRunsToday());
    // Authoritative verdict from the run API (null until it has answered;
    // localStorage counting is only a fallback when the API is unavailable).
    const [serverLimit, setServerLimit] = useState(null);
    const pendingRun = useRef(false);

    useEffect(() => {
        window.scrollTo(0, 0);
        const prev = document.title;
        document.title = 'TrueRep | Sponsorship Price Calculator';
        return () => {
            document.title = prev;
        };
    }, []);

    // Hydrate from an emailed results link. Ready runs load straight into the
    // normal results view (CPM slider and calc type stay live on the loaded
    // data); still-running or failed runs explain themselves in place.
    useEffect(() => {
        if (!runUid) return;
        let cancelled = false;
        (async () => {
            try {
                const res = await fetch(`${API_BASE}/result?uid=${encodeURIComponent(runUid)}`);
                const data = await res.json().catch(() => null);
                if (cancelled || !data) return;
                if (res.ok && data.status === 'ready') {
                    const channel = { ...data.channel, platform: data.platform };
                    const label =
                        data.platform === 'YouTube'
                            ? CONTENT_TYPES.find((o) => o.value === data.params?.contentType)?.label ||
                              'Any Type'
                            : 'Recent posts';
                    setChannelOptions([channel]);
                    setSelectedChannel(channel);
                    if (data.params?.videoCount) setVideoCount(String(data.params.videoCount));
                    if (data.platform === 'YouTube' && data.params?.contentType) {
                        setContentType(data.params.contentType);
                    }
                    setFetched({
                        manual: false,
                        channel,
                        videos: data.videos || [],
                        contentTypeLabel: label,
                        // What the run asked for — the live videoCount input
                        // can be edited afterwards, so keep it with the data.
                        requested: Number(data.params?.videoCount) || null,
                    });
                } else if (res.ok && data.status === 'queued') {
                    setQueuedNotice({ channelTitle: data.channel?.channelTitle, pending: true });
                } else {
                    setError(
                        data.error ||
                            "We couldn't load that analysis. It may have been removed — run a new one below."
                    );
                }
            } catch (e) {
                if (!cancelled) {
                    setError("We couldn't load that analysis right now — please try the link again shortly.");
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [runUid]);

    const activePlatform = selectedChannel?.platform || 'YouTube';
    // Manual entry applies when explicitly degraded, or for YouTube without a
    // browser key (the lookup/picker can't resolve a channel to analyze).
    // Instagram/TikTok resolve their handle from the URL alone, so a missing
    // YouTube key doesn't force them manual.
    const isManual = manualMode || (activePlatform === 'YouTube' && !YT_API_KEY);
    // Unlimited status is decided server-side when the lead is captured (API
    // response or the hashed test email) — no email list ships in the bundle.
    const unlimited = !!lead?.unlimited;
    // The server verdict wins; the localStorage counter only gates when the
    // API hasn't answered (not deployed / not configured).
    const limitReached = serverLimit ?? (!unlimited && runsToday >= DAILY_RUN_LIMIT);

    // ----- Creator lookup ----------------------------------------------------

    const findChannel = async (e) => {
        if (e) e.preventDefault();
        setError('');
        setChannelOptions([]);
        setSelectedChannel(null);
        setFetched(null);
        setManualMode(false);
        setManualNote('');
        setQueuedNotice(null);

        const detected = detectLink(link.trim());
        if (!detected) {
            setError(
                'Please enter a YouTube, Instagram, or TikTok channel link (e.g., youtube.com/@mkbhd, instagram.com/mkbhd, tiktok.com/@mkbhd).'
            );
            return;
        }

        // Instagram/TikTok resolve purely from the link — the confirmation
        // chip echoes the handle back so a typo is visible before the run.
        if (detected.platform !== 'YouTube') {
            const channel = profileChannel(detected.platform, detected.handle);
            setChannelOptions([channel]);
            setSelectedChannel(channel);
            return;
        }

        const ident = detected.ident;
        const manualChannel = {
            platform: 'YouTube',
            channelTitle: `@${ident.value.replace(/^@/, '')}`,
            channelLink: link.trim().startsWith('http') ? link.trim() : `https://${link.trim()}`,
        };

        if (!YT_API_KEY) {
            setSelectedChannel(manualChannel);
            setManualMode(true);
            setManualNote('Live stats are unavailable right now — enter the expected views below and we\'ll price it the same way.');
            return;
        }

        setLookupLoading(true);
        try {
            const options = await findYouTubeChannels(ident);
            if (!options.length) {
                setError("We couldn't find that channel. Double-check the link and try again.");
            } else {
                setChannelOptions(options);
                setSelectedChannel(options[0]);
            }
        } catch (err) {
            setSelectedChannel(manualChannel);
            setManualMode(true);
            setManualNote('We couldn\'t reach YouTube right now — enter the expected views below and we\'ll price it the same way.');
        } finally {
            setLookupLoading(false);
        }
    };

    // ----- Run the model -----------------------------------------------------

    const bumpRuns = () => {
        const count = readRunsToday() + 1;
        writeStorage(RUNS_KEY, { date: today(), count });
        setRunsToday(count);
    };

    // Legacy client-side gate + pull, kept as the fallback for when the
    // server pipeline isn't available (API not deployed, or the platform's
    // pull/email service isn't configured): the run API gates when reachable
    // (localStorage counting otherwise), then YouTube stats are pulled with
    // the browser key exactly as the tool always did.
    const runSyncFallback = async (activeLead) => {
        let gate = null;
        try {
            gate = await api('run', {
                email: activeLead?.email,
                channelId: selectedChannel?.channelId || null,
                channelTitle: selectedChannel?.channelTitle || null,
            });
        } catch (err) {
            gate = null; // API unavailable — fall back to local counting.
        }
        if (gate) {
            setServerLimit(!gate.allowed);
            if (!gate.allowed) return;
        } else if (!activeLead?.unlimited) {
            if (readRunsToday() >= DAILY_RUN_LIMIT) {
                setRunsToday(readRunsToday());
                return;
            }
            bumpRuns();
        }

        if (isManual) {
            setFetched({ manual: true, channel: selectedChannel });
            return;
        }
        const count = Math.max(1, Math.min(MAX_VIDEO_COUNT, Math.round(num(videoCount)) || 1));
        const videos = await fetchYouTubeVideos(selectedChannel, contentType, count);
        if (!videos.length) {
            throw new Error(
                contentType === 'any'
                    ? 'No public videos found for this channel.'
                    : 'No videos of the selected content type were found for this channel.'
            );
        }
        setFetched({
            manual: false,
            channel: selectedChannel,
            videos,
            contentTypeLabel:
                CONTENT_TYPES.find((o) => o.value === contentType)?.label || 'Any Type',
        });
    };

    // The gated action (mirrors the Retool getChannelData trigger): assumes
    // the lead gate was already passed. Inputs are validated first so an
    // invalid run never consumes a credit. All live analyses now queue
    // through the enqueue API for email delivery — the server gates the run
    // atomically in Neon (the DB is authoritative, so clearing browser
    // storage doesn't reset it), pulls the stats (YouTube via the Data API,
    // Instagram/TikTok via Scrape Creators), and emails a results link.
    // Manual entry keeps the old local flow. `activeLead` is passed
    // explicitly when running right after lead capture, before the state
    // re-render lands.
    const executeRun = async (activeLead = lead) => {
        if (isManual && num(manualViews) <= 0) {
            setError('Enter the expected views per video to run the model.');
            return;
        }
        if (!isManual && !selectedChannel) {
            setError('Find and select a channel first.');
            return;
        }

        setLoading(true);
        setQueuedNotice(null);
        try {
            if (isManual) {
                await runSyncFallback(activeLead);
                return;
            }

            const count = Math.max(1, Math.min(MAX_VIDEO_COUNT, Math.round(num(videoCount)) || 1));
            const platform = selectedChannel.platform || 'YouTube';
            try {
                const data = await api('enqueue', {
                    email: activeLead?.email,
                    platform,
                    channelId: selectedChannel.channelId || null,
                    channelTitle: selectedChannel.channelTitle || null,
                    channelLink: selectedChannel.channelLink || null,
                    image: selectedChannel.image || null,
                    videoCount: count,
                    contentType: platform === 'YouTube' ? contentType : null,
                });
                if (data && data.allowed === false) {
                    setServerLimit(true);
                    return;
                }
                setServerLimit(false);
                setFetched(null);
                setQueuedNotice({
                    email: activeLead?.email,
                    channelTitle: selectedChannel.channelTitle,
                });
            } catch (err) {
                if (err.status === 501) {
                    // Server pipeline unavailable for this platform — degrade
                    // the way the tool always has: client-side YouTube pull
                    // when the browser key exists, manual entry otherwise.
                    if (platform === 'YouTube' && YT_API_KEY) {
                        await runSyncFallback(activeLead);
                    } else {
                        setManualMode(true);
                        setManualNote(
                            "Live stats are unavailable right now — enter the expected views below and we'll price it the same way."
                        );
                    }
                } else {
                    throw err;
                }
            }
        } catch (err) {
            setError(err.message || 'Something went wrong — please try again.');
        } finally {
            setLoading(false);
        }
    };

    const runAnalysis = () => {
        setError('');
        if (!lead) {
            pendingRun.current = true;
            setLeadOpen(true);
            return;
        }
        if (limitReached) return;
        executeRun();
    };

    const onLeadDone = (newLead) => {
        setLead(newLead);
        setLeadOpen(false);
        if (pendingRun.current) {
            pendingRun.current = false;
            // executeRun gates itself (server-side when the API is up), using
            // the lead we just captured rather than the stale render.
            executeRun(newLead);
        }
    };

    // ----- Derived numbers ---------------------------------------------------

    // The analyzed set is locked to the videos pulled at Calculate time —
    // changing content type or video count only applies to the next run.
    const analyzedVideos = useMemo(
        () => (fetched && !fetched.manual ? fetched.videos : []),
        [fetched]
    );

    const calc = calcType === 'median' ? median : average;

    const expectedViews = fetched
        ? fetched.manual
            ? num(manualViews)
            : calc(analyzedVideos.map((v) => v.viewCount))
        : 0;

    // Workflow parity: engagement = total (likes + comments) / total views
    // across the consideration set, not an average of per-video rates.
    const engagementRate = useMemo(() => {
        if (!fetched) return 0;
        if (fetched.manual) return num(manualEngage) / 100;
        const totalViews = analyzedVideos.reduce((a, v) => a + v.viewCount, 0);
        const totalEngagements = analyzedVideos.reduce((a, v) => a + v.likeCount + v.commentCount, 0);
        return totalViews > 0 ? totalEngagements / totalViews : 0;
    }, [fetched, manualEngage, analyzedVideos]);

    // Rate calculation (ported from the Retool rateCalculation): fee = CPM x
    // views / 1000 at the slider's floor and ceiling.
    const lowCPM = Math.min(num(cpmMin), num(cpmMax));
    const highCPM = Math.max(num(cpmMin), num(cpmMax));
    const minFee = (lowCPM * expectedViews) / 1000;
    const maxFee = (highCPM * expectedViews) / 1000;

    const hasResult = !!fetched && expectedViews > 0;

    const tableTotals = useMemo(() => {
        const views = analyzedVideos.reduce((a, v) => a + v.viewCount, 0);
        const likes = analyzedVideos.reduce((a, v) => a + v.likeCount, 0);
        const comments = analyzedVideos.reduce((a, v) => a + v.commentCount, 0);
        return { views, likes, comments, engage: views > 0 ? (likes + comments) / views : 0 };
    }, [analyzedVideos]);

    const downloadCsv = () => {
        const esc = (c) => `"${String(c ?? '').replace(/"/g, '""')}"`;
        const header = ['Video title', 'Video link', 'Publish date', 'Views', 'Likes', 'Comments', 'Engagement rate'];
        const rows = analyzedVideos.map((v) => [
            v.videoTitle,
            v.videoLink,
            v.publishDate,
            v.viewCount,
            v.likeCount,
            v.commentCount,
            (v.engageRate * 100).toFixed(2) + '%',
        ]);
        const csv = [header, ...rows].map((r) => r.map(esc).join(',')).join('\n');
        const blob = new Blob([csv], { type: 'text/csv' });
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `${(fetched?.channel?.channelTitle || 'channel').replace(/[^A-Za-z0-9@_-]+/g, '-')}-videos.csv`;
        a.click();
        URL.revokeObjectURL(a.href);
    };

    return (
        <section className="pm">
            <h2 className="pm-title">Sponsorship Price Calculator</h2>
            <p className="pm-intro">
                Use this tool to figure out how much you should be paying for a video sponsorship on
                YouTube, Instagram, or TikTok.
            </p>
            <p className="pm-intro">
                Simply add a link to the channel you want to analyze, adjust your parameters, and get
                your data.
            </p>

            <div className="pm-form-block">
                <span className="pm-label">Input a YouTube, Instagram, or TikTok channel link</span>
                <form className="pm-link-row" onSubmit={findChannel}>
                    <div className="pm-input-wrap pm-link-input">
                        <input
                            type="text"
                            placeholder="Enter link"
                            value={link}
                            onChange={(e) => setLink(e.target.value)}
                        />
                    </div>
                    <button type="submit" className="pm-btn pm-btn-find" disabled={!link.trim() || lookupLoading}>
                        {lookupLoading ? 'Searching…' : 'Find Channel'}
                    </button>
                </form>

                {channelOptions.length > 0 && (
                    <ChannelPicker
                        options={channelOptions}
                        selected={selectedChannel}
                        onSelect={setSelectedChannel}
                    />
                )}

                {manualMode && selectedChannel && (
                    <div className="pm-note">
                        <strong>{selectedChannel.channelTitle}</strong> · {manualNote}
                    </div>
                )}
            </div>

            {!isManual ? (
                <div className={`pm-row-3 ${activePlatform !== 'YouTube' ? 'pm-row-2' : ''}`}>
                    <Field
                        label={`Video count (max ${MAX_VIDEO_COUNT})`}
                        type="number"
                        min="1"
                        max={MAX_VIDEO_COUNT}
                        suffix="videos"
                        title="The number of videos to analyze, starting from the most recent. Max: 10"
                        value={videoCount}
                        onChange={(e) => {
                            const v = e.target.value;
                            setVideoCount(
                                v === '' ? '' : String(Math.max(1, Math.min(MAX_VIDEO_COUNT, Math.round(num(v)) || 1)))
                            );
                        }}
                    />
                    <label className="pm-field">
                        <span>Calculation type</span>
                        <div className="pm-input-wrap">
                            <select
                                value={calcType}
                                onChange={(e) => setCalcType(e.target.value)}
                                title="Expected views will be calculated by taking the average or median view count of the videos in the consideration set"
                            >
                                <option value="average">Average</option>
                                <option value="median">Median</option>
                            </select>
                        </div>
                    </label>
                    {/* Longform/Shorts filtering is a YouTube upload-playlist
                        feature; Instagram/TikTok pulls are simply the most
                        recent posts, so the selector is hidden for them. */}
                    {activePlatform === 'YouTube' && (
                        <label className="pm-field">
                            <span>Content type</span>
                            <div className="pm-input-wrap">
                                <select value={contentType} onChange={(e) => setContentType(e.target.value)}>
                                    {CONTENT_TYPES.map((o) => (
                                        <option key={o.value} value={o.value}>{o.label}</option>
                                    ))}
                                </select>
                            </div>
                        </label>
                    )}
                </div>
            ) : (
                <div className="pm-row-3 pm-row-2">
                    <Field
                        label="Expected views per video"
                        type="number"
                        min="0"
                        value={manualViews}
                        onChange={(e) => setManualViews(e.target.value)}
                    />
                    <Field
                        label="Engagement rate (optional)"
                        type="number"
                        min="0"
                        step="any"
                        suffix="%"
                        value={manualEngage}
                        onChange={(e) => setManualEngage(e.target.value)}
                    />
                </div>
            )}

            <CpmSlider
                low={cpmMin}
                high={cpmMax}
                onChange={(lo, hi) => {
                    setCpmMin(String(lo));
                    setCpmMax(String(hi));
                }}
            />
            <p className="pm-footnote">
                <i>
                    Keep in mind that actual CPMs will vary: this tool is meant to help you calculate an
                    expected CPM, not provide guidance on a suggested rate. Actual rates will vary based
                    on creator, negotiated terms, and other factors.
                </i>
            </p>

            <button
                className="pm-btn pm-calculate"
                onClick={runAnalysis}
                disabled={loading || limitReached || (!isManual && !selectedChannel)}
            >
                {loading ? 'Crunching the numbers…' : 'Calculate'}
            </button>
            {limitReached && (
                <p className="pm-limit">
                    Sorry, you've reached your daily limit for analyses. Come back tomorrow, or sign up
                    to get unlimited pricing analyses and more with TrueRep.
                </p>
            )}
            {error && <p className="pm-error">{error}</p>}
            {queuedNotice && !fetched && (
                <p className="pm-note">
                    {queuedNotice.pending ? (
                        <>
                            This analysis{queuedNotice.channelTitle ? <> of <strong>{queuedNotice.channelTitle}</strong></> : ''} is
                            still running — we'll email you a link to the results as soon as it's
                            ready (usually within a few minutes).
                        </>
                    ) : (
                        <>
                            We're on it! We're pulling{' '}
                            <strong>{queuedNotice.channelTitle || 'this creator'}</strong>'s data now and
                            will email your results to <strong>{queuedNotice.email}</strong> — usually
                            within a few minutes. Check your spam folder if it doesn't show up, and feel
                            free to close this page in the meantime.
                        </>
                    )}
                </p>
            )}
            {hasResult && !fetched.manual && fetched.requested > analyzedVideos.length && (
                <p className="pm-note">
                    {(fetched.channel?.platform || 'YouTube') === 'YouTube' ? (
                        <>
                            Only <strong>{analyzedVideos.length}</strong> of the {fetched.requested} videos
                            you asked for {analyzedVideos.length === 1 ? 'was' : 'were'} available for this
                            channel, so the analysis is based on
                            {analyzedVideos.length === 1 ? ' that video' : ' those videos'}.
                        </>
                    ) : (
                        <>
                            Only <strong>{analyzedVideos.length}</strong> of the {fetched.requested} posts
                            you asked for {analyzedVideos.length === 1 ? 'was' : 'were'} found (pinned
                            posts aren't counted), so the analysis is based on
                            {analyzedVideos.length === 1 ? ' that post' : ' those posts'}.
                        </>
                    )}
                </p>
            )}

            <div className="pm-stats">
                <StatTile
                    label="Estimated views"
                    value={number(expectedViews)}
                    caption={
                        hasResult
                            ? fetched.manual
                                ? 'Manually entered'
                                : `${calcType === 'median' ? 'Median' : 'Average'} of ${analyzedVideos.length} recent ${(fetched.channel?.platform || 'YouTube') === 'YouTube' ? 'video' : 'post'}${analyzedVideos.length === 1 ? '' : 's'}${(fetched.channel?.platform || 'YouTube') === 'YouTube' ? ` (${(fetched.contentTypeLabel || 'Any Type').toLowerCase()})` : ''}`
                            : null
                    }
                />
                <StatTile
                    label="Engagement rate"
                    value={percent(engagementRate)}
                    caption="Likes + comments ÷ views"
                />
                <StatTile label={`Min fee (${currency(lowCPM)} CPM)`} value={currency(minFee)} />
                <StatTile label={`Max fee (${currency(highCPM)} CPM)`} value={currency(maxFee)} />
            </div>

            {hasResult && !fetched.manual && analyzedVideos.length > 0 && (
                <div className="pm-table-card">
                    <div className="pm-table-head">
                        <h3>Videos Analyzed</h3>
                        <button className="pm-btn pm-btn-outline" onClick={downloadCsv}>Download CSV</button>
                    </div>
                    <div className="pm-table-scroll">
                        <table className="pm-table">
                            <thead>
                                <tr>
                                    <th></th>
                                    <th>Video Title</th>
                                    <th>Publish Date</th>
                                    <th className="pm-num">Views</th>
                                    <th className="pm-num">Likes</th>
                                    <th className="pm-num">Comments</th>
                                    <th className="pm-num">Engagement Rate</th>
                                </tr>
                            </thead>
                            <tbody>
                                {analyzedVideos.map((v) => (
                                    <tr key={v.videoId}>
                                        <td>
                                            <a href={v.videoLink} target="_blank" rel="noreferrer">
                                                {v.thumbnail ? <img src={v.thumbnail} alt="" className="pm-thumb" /> : '—'}
                                            </a>
                                        </td>
                                        <td className="pm-title-cell">
                                            <a href={v.videoLink} target="_blank" rel="noreferrer">{v.videoTitle}</a>
                                        </td>
                                        <td>{v.publishDate ? new Date(v.publishDate).toLocaleDateString() : '—'}</td>
                                        <td className="pm-num">{compact(v.viewCount)}</td>
                                        <td className="pm-num">{compact(v.likeCount)}</td>
                                        <td className="pm-num">{compact(v.commentCount)}</td>
                                        <td className="pm-num">{percent(v.engageRate)}</td>
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot>
                                <tr>
                                    <td></td>
                                    <td>Total</td>
                                    <td></td>
                                    <td className="pm-num">{compact(tableTotals.views)}</td>
                                    <td className="pm-num">{compact(tableTotals.likes)}</td>
                                    <td className="pm-num">{compact(tableTotals.comments)}</td>
                                    <td className="pm-num">{percent(tableTotals.engage)} avg</td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                </div>
            )}

            <Popup trigger={leadOpen} onClose={() => setLeadOpen(false)}>
                <LeadCaptureForm
                    channelName={selectedChannel?.channelTitle}
                    onDone={onLeadDone}
                />
            </Popup>
        </section>
    );
};

export default PriceModelB;
